import { Menu, Transition } from '@headlessui/react';
import clsx from 'clsx';
import * as React from 'react';
import { Fragment } from 'react';
import ChevronDown from '../../../../../../../Cutesies/ChevronDown';
import { T } from '../../../../../../../../Translations';

interface FilterOption {
  id: number;
  name: string;
  count: number;
}

type FilterLabel = 'Competency' | 'Product Type';

interface Props {
  label: FilterLabel;
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  /** Optional: transform the option name for display */
  getDisplayLabel?: (name: string) => string;
  /** Optional: intercom target for analytics */
  'data-intercom-target'?: string;
}

/**
 * FilterDropdown follows the program catalog pattern:
 * - selectedValues contains the currently selected option names
 * - When selectedValues equals all option names, "All" is checked
 * - Clicking an item toggles it (adds if missing, removes if present)
 * - Clicking "All" selects all items
 */
const FilterDropdown: React.FC<Props> = ({
  label,
  options,
  selectedValues,
  onChange,
  getDisplayLabel,
  'data-intercom-target': intercomTarget,
}) => {
  const allNames = options.map((o) => o.name);

  // Helper to check if arrays contain the same elements (order-independent)
  const arraysEqual = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  };

  const allSelected = arraysEqual(selectedValues, allNames);

  const handleToggle = (optionName: string) => {
    if (selectedValues.includes(optionName)) {
      // Remove this option
      const newValues = selectedValues.filter((v) => v !== optionName);
      onChange(newValues);
    } else {
      // Add this option
      onChange([...selectedValues, optionName]);
    }
  };

  const handleSelectAll = () => {
    // Toggle: if all selected, deselect all; otherwise select all
    if (allSelected) {
      onChange([]);
    } else {
      onChange(allNames);
    }
  };

  const isChecked = (optionName: string): boolean => {
    return selectedValues.includes(optionName);
  };

  return (
    <Menu as="div" className="relative" data-intercom-target={intercomTarget}>
      <Menu.Button
        className={clsx(
          'mt-1 block w-48 rounded-md border-1 border-solid',
          'border-border bg-card px-3 py-2 text-sm font-medium',
        )}
      >
        <div className="flex flex-row justify-between w-full">
          {allSelected ? (
            <T kind={label} />
          ) : (
            <T kind="Selected: ({{count}})" count={selectedValues.length} />
          )}
          <div className="w-5">
            <ChevronDown />
          </div>
        </div>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={clsx(
            'bg-card drop-shadow-md rounded-md mt-2 border-1',
            'border-solid border-border py-2 pl-2 pr-1 z-[3] absolute',
          )}
        >
          <div className="py-1 max-h-96 relative overflow-scroll overflow-x-hidden">
            {/* Select All option */}
            <button onClick={handleSelectAll} className="flex w-full py-1">
              <div className="flex flex-row bg-card text-foreground m-1 justify-between w-full">
                <div className="flex flex-row pr-6">
                  <div className="pr-2">
                    <div className="flex h-6 items-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="text-sm">
                    <T kind="All" />
                  </div>
                </div>
                <div className="text-sm mr-3">{allNames.length}</div>
              </div>
            </button>

            {/* Individual options */}
            {options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleToggle(option.name)}
                className="flex w-full py-1"
              >
                <div className="flex flex-row bg-card text-foreground m-1 justify-between w-full">
                  <div className="flex flex-row pr-6">
                    <div className="pr-2">
                      <div className="flex h-6 items-center">
                        <input
                          type="checkbox"
                          checked={isChecked(option.name)}
                          className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="text-sm">
                      {getDisplayLabel ? getDisplayLabel(option.name) : option.name}
                    </div>
                  </div>
                  <div className="text-sm mr-3">{option.count}</div>
                </div>
              </button>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default FilterDropdown;
